import {
  BellOff,
  Calculator,
  CircleCheck,
  FileText,
  Gavel,
  MessageSquare,
  Paperclip,
  Receipt,
  TriangleAlert,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Drawer'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import {
  groupByRecord,
  useMarkNotificationsRead,
  useNotifications,
} from '@/features/notifications/api/useNotifications'
import { wordNotification } from '@/features/notifications/notificationMessages'
import { notificationTitleKey } from '@/features/notifications/notificationTitles'
import type { NotificationKind } from '@/types/domain'
import { useTranslation } from 'react-i18next'

/* The notification centre (FR-49): a panel grouping events by record, with
 * unread state, covering quote and case events alike.
 *
 * Grouping is the whole point. A flat reverse-chronological list of the same
 * events interleaves three records and makes the reader reassemble each one in
 * their head — which is precisely the work the panel exists to remove. AB-27
 * with four updates is ONE thing to read.
 *
 * Founder finance events appear here and on the dashboard only, never by email
 * (FR-50). The daily email digest is generated from this same feed.
 */

/** Every event type carries an icon and a word. Unread state is likewise
 *  marked by a dot AND by weight — colour never carries meaning alone. */
const KIND_ICONS: Record<NotificationKind, LucideIcon> = {
  message: MessageSquare,
  status: CircleCheck,
  approval: Gavel,
  payment: Wallet,
  expense: Receipt,
  pricing: Calculator,
  document: FileText,
  file: Paperclip,
  assignment: UserPlus,
  stuck: TriangleAlert,
}

export interface NotificationDrawerProps {
  open: boolean
  onClose: () => void
}

export function NotificationDrawer({ open, onClose }: NotificationDrawerProps) {
  const { t } = useTranslation()
  const format = useFormat()
  const { data, isPending, isError, error, refetch } = useNotifications()
  const markRead = useMarkNotificationsRead()

  const groups = useMemo(() => groupByRecord(data?.items ?? []), [data?.items])
  const unreadCount = data?.unreadCount ?? 0

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('common.notifications.label')}
      action={
        unreadCount > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markRead.mutate(undefined)}
            disabled={markRead.isPending}
          >
            {t('common.notifications.markAllRead')}
          </Button>
        ) : null
      }
    >
      {isPending ? (
        <div className="flex flex-col gap-3 p-4" aria-busy="true" aria-label={t('common.notifications.loading')}>
          <Skeleton className="h-20 rounded-[var(--radius-DEFAULT)]" />
          <Skeleton className="h-28 rounded-[var(--radius-DEFAULT)]" />
          <Skeleton className="h-20 rounded-[var(--radius-DEFAULT)]" />
        </div>
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} className="m-4 border-0" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={BellOff}
          title={t('common.notifications.emptyTitle')}
          description={t('common.notifications.emptyDescription')}
          className="py-16"
        />
      ) : (
        <ul className="flex flex-col">
          {groups.map((group) => (
            <li key={`${group.recordKind}:${group.recordNumber}`}>
              <section className="border-line-hair border-b">
                {/* The group header names the record once, so the events below
                    it do not each repeat it. */}
                <div className="flex items-baseline gap-2 px-4 pt-3.5 pb-1.5">
                  <Link
                    to={group.href}
                    onClick={onClose}
                    className="text-brand shrink-0 text-[13px] font-semibold no-underline hover:underline"
                  >
                    {group.recordNumber}
                  </Link>
                  <span className="asa-eyebrow shrink-0">
                    {t(`recordKind.${group.recordKind}`)}
                  </span>
                  <span className="text-text-muted min-w-0 flex-1 truncate text-[12px] font-light">
                    {group.recordTitle}
                  </span>
                  {group.unreadCount > 0 ? (
                    <span className="bg-brand-soft text-brand asa-num shrink-0 rounded-full px-1.5 py-px text-[11px] font-semibold">
                      {group.unreadCount}
                    </span>
                  ) : null}
                </div>

                <ul className="flex flex-col pb-2">
                  {group.items.map((item) => {
                    const KindIcon = KIND_ICONS[item.kind]
                    // Worded here, at render — the cache outlives a language
                    // switch. A row's code and params first (story B5); a row
                    // the codes cannot word falls back to the exact-title
                    // table, which knows a topic and its English title; and
                    // anything else is the server's own sentence.
                    const coded = wordNotification(item, t, format.date)
                    const titleKey = coded ? undefined : notificationTitleKey(item)
                    const title = coded?.title ?? (titleKey ? t(titleKey) : item.title)
                    const detail = coded ? coded.detail : item.detail
                    return (
                      <li key={item.id}>
                        <Link
                          to={item.href}
                          onClick={() => {
                            if (!item.read) markRead.mutate(item.id)
                            onClose()
                          }}
                          className={cn(
                            'flex gap-2.5 px-4 py-2 no-underline transition-colors',
                            'duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
                            'hover:bg-surface-hover hover:no-underline',
                          )}
                        >
                          <KindIcon
                            size={15}
                            aria-hidden
                            className={cn(
                              'mt-0.5 shrink-0',
                              item.kind === 'stuck'
                                ? 'text-attention'
                                : item.read
                                  ? 'text-text-faint'
                                  : 'text-text-muted',
                            )}
                          />

                          <span className="flex min-w-0 flex-1 flex-col">
                            <span
                              className={cn(
                                'text-[13px] leading-[1.45]',
                                // Unread is weight AND a dot, never colour alone.
                                item.read
                                  ? 'text-text-body font-normal'
                                  : 'text-text-heading font-medium',
                              )}
                            >
                              {title}
                            </span>
                            <span className="text-text-muted text-[12px] leading-[1.4] font-light">
                              {detail}
                            </span>
                          </span>

                          <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
                            {/* The exact instant lives in the title attribute;
                                the visible label stays terse. */}
                            <time
                              dateTime={item.at}
                              title={format.timestamp(item.at)}
                              className="text-text-faint text-[11.5px] font-normal whitespace-nowrap"
                            >
                              {format.relative(item.at)}
                            </time>
                            {item.read ? (
                              // A fixed-size placeholder keeps the timestamps
                              // in one column whether or not a dot is present.
                              <span className="size-[6px]" aria-hidden />
                            ) : (
                              <>
                                <span
                                  aria-hidden
                                  className="bg-brand size-[6px] shrink-0 rounded-full"
                                />
                                <span className="sr-only">
                                  {t('common.notifications.unreadItem', {
                                    kind: t(`notificationKind.${item.kind}`),
                                  })}
                                </span>
                              </>
                            )}
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </section>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  )
}
